import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Calendar,
  Clock,
  Eye,
  Mail,
  Settings,
  Shield,
  User,
  List,
  ArrowRight,
  Check,
  Layout,
  Palette,
  Zap,
  Users,
  Link2,
  Lock,
  Globe,
} from "lucide-react";
import type { AudienceMode } from "@core/types/poll";
// MIGRATION Phase 5: Types produits migrés vers apps
// PollSettingsForm est générique et utilise any pour settings
// import type { DatePollSettings } from "@core/types/products/date-polls";
// import type { FormPollSettings } from "@core/types/products/form-polls";
import { ThemeSelector } from "./ThemeSelector";
import { Button } from "@core/components/ui/button";
import { Feature } from "@core/components/Feature";
import { GroupSelector } from "../groups/GroupSelector";
import { PendingPollRestoreModal } from "./PendingPollRestoreModal";
import { usePendingPollRestore } from "@core/hooks/usePendingPollRestore";

interface PollSettingsFormProps {
  settings: any; // Générique - accepte tous types de settings produits
  onSettingsChange: (settings: any) => void;
  pollType: "date" | "form" | "availability";
  // Props additionnelles pour les formulaires
  themeId?: string;
  onThemeChange?: (id: string) => void;
  displayMode?: "all-at-once" | "multi-step";
  onDisplayModeChange?: (mode: "all-at-once" | "multi-step") => void;
  resultsVisibility?: "restricted" | "public" | "voters" | "creator-only";
  onResultsVisibilityChange?: (
    vis: "restricted" | "public" | "voters" | "creator-only",
  ) => void;
  // Données du sondage pour sauvegarde avant connexion
  currentPollData?: any;
  /** Masque la section audience (déplacée up-front dans le PollCreator). */
  hideAudience?: boolean;
  /** Débloque la publication sur la page personnelle (SOLO+). */
  hasPaidPlan?: boolean;
}

type TabType =
  | "basic"
  | "advanced"
  | "email"
  | "visibility"
  | "theme"
  | "display"
  | "participants";

export default function PollSettingsForm({
  settings,
  onSettingsChange,
  pollType,
  themeId,
  onThemeChange,
  displayMode,
  onDisplayModeChange,
  resultsVisibility,
  onResultsVisibilityChange,
  currentPollData,
  hideAudience = false,
  hasPaidPlan = false,
}: PollSettingsFormProps) {
  const { t } = useTranslation("polls");
  const [activeTab, setActiveTab] = useState<TabType>("basic");

  // Vocabulaire adapté au produit : sur DooQuest (formulaires) on dit
  // "formulaire"/"répondre" au lieu de "sondage"/"voter". Les clés de base
  // restent inchangées pour date-polls ; on bascule sur une variante "<clé>Form"
  // uniquement pour les formulaires.
  const isForm = pollType === "form";
  const tForm = (baseKey: string, opts?: Record<string, unknown>): string =>
    isForm ? t(`${baseKey}Form`, opts) : t(baseKey, opts);

  // Hook pour la restauration des sondages en attente
  const { hasPendingPoll, pendingPoll, isRestoring, restorePoll, dismissPoll } =
    usePendingPollRestore();

  // Mode de partage : groupe, emails directs, ou lien/QR.
  // On infère depuis les anciens settings pour préserver les sondages déjà créés.
  const audienceMode: AudienceMode = useMemo(() => {
    if (settings.audienceMode) return settings.audienceMode as AudienceMode;
    if (settings.audienceGroupId) return "group";
    if ((settings.inviteEmails || "").trim().length > 0) return "emails";
    return "link";
  }, [settings.audienceMode, settings.audienceGroupId, settings.inviteEmails]);

  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    {
      id: "basic",
      label: t("settings.tab.general"),
      icon: <Settings className="w-4 h-4" />,
    },
    // { id: "advanced", label: "Avancé", icon: <Zap className="w-4 h-4" /> },
    {
      id: "visibility",
      label: t("settings.tab.access"),
      icon: <Shield className="w-4 h-4" />,
    },
  ].filter((tab) => {
    if (pollType === "availability") {
      return !["visibility", "advanced"].includes(tab.id);
    }
    return true;
  }) as { id: TabType; label: string; icon: React.ReactNode }[];

  // Ajouter les onglets spécifiques aux formulaires
  if (pollType === "form") {
    tabs.splice(1, 0, {
      id: "display",
      label: t("settings.tab.display"),
      icon: <Layout className="w-4 h-4" />,
    });
    tabs.splice(2, 0, {
      id: "theme",
      label: t("settings.tab.theme"),
      icon: <Palette className="w-4 h-4" />,
    });
  }

  // Plus d'onglet participants séparé, c'est fusionné dans "visibility" (Accès)

  const updateSetting = (key: string, value: any) => {
    onSettingsChange({
      ...settings,
      [key]: value,
    });
  };

  const renderBasicSettings = () => (
    <div className="space-y-6">
      {/* Champ lieu - pour les sondages de date */}
      {pollType === "date" && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground">
            📍 {t("settings.location.heading")}
          </h3>
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <div className="flex-1">
                <label
                  htmlFor="eventLocation"
                  className="block text-sm font-medium text-muted-foreground mb-1"
                >
                  {t("settings.location.label")}
                </label>
                <input
                  type="text"
                  id="eventLocation"
                  placeholder={t("settings.location.placeholder")}
                  value={settings.eventLocation || ""}
                  onChange={(e) =>
                    updateSetting("eventLocation", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {t("settings.location.hint")}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Publication sur la page personnelle — réservée SOLO+, indépendante
          du choix de modèle (fait up-front dans le chip Template). */}
      {pollType === "date" && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground">
            {t("settings.publish.heading")}
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Globe className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="font-medium text-foreground">
                    {t("settings.publish.title")}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t("settings.publish.description")}
                  </p>
                </div>
              </div>
              {hasPaidPlan ? (
                <label
                  htmlFor="publishToPersonalPage"
                  className="relative inline-flex items-center cursor-pointer"
                >
                  <input
                    type="checkbox"
                    id="publishToPersonalPage"
                    className="sr-only peer"
                    checked={settings.publishToPersonalPage ?? false}
                    onChange={(e) =>
                      updateSetting("publishToPersonalPage", e.target.checked)
                    }
                  />
                  <div className="w-11 h-6 bg-input peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-ring/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-background after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-background after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              ) : (
                <Link
                  to="/pricing"
                  className="flex items-center gap-1 text-xs text-primary hover:text-primary/80"
                >
                  <Lock className="w-3 h-3" />
                  {t("settings.publish.upgradeCta")}
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground">
          {t("settings.email.notificationHeading")}
        </h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Mail className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="font-medium text-foreground">
                  {t("settings.email.copyTitle")}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t("settings.email.copyDescription")}
                </p>
              </div>
            </div>
            <label
              htmlFor="sendEmailCopy"
              className="relative inline-flex items-center cursor-pointer"
            >
              <input
                type="checkbox"
                id="sendEmailCopy"
                className="sr-only peer"
                checked={settings.sendEmailCopy ?? false}
                onChange={(e) =>
                  updateSetting("sendEmailCopy", e.target.checked)
                }
              />
              <div className="w-11 h-6 bg-input peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-ring/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-background after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-background after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>

          {settings.sendEmailCopy && (
            <div className="ml-8">
              <label
                htmlFor="resultsCopyEmailBasic"
                className="block text-sm font-medium text-muted-foreground mb-1"
              >
                {t("settings.email.addressLabel")}
              </label>
              <input
                type="email"
                id="resultsCopyEmailBasic"
                placeholder={t("settings.email.addressPlaceholder")}
                value={settings.resultsCopyEmail || ""}
                onChange={(e) =>
                  updateSetting("resultsCopyEmail", e.target.value)
                }
                className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                required={settings.sendEmailCopy}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t("settings.email.addressRequiredHint")}
              </p>
            </div>
          )}

          {/* Override par sondage de la fréquence de notification de vote.
              "" = hériter du réglage global du compte (profiles.vote_notification_mode). */}
          <div className="pt-2">
            <label
              htmlFor="voteNotificationMode"
              className="block text-sm font-medium text-muted-foreground mb-1"
            >
              {tForm("settings.notify.label")}
            </label>
            <select
              id="voteNotificationMode"
              aria-label={tForm("settings.notify.ariaLabel")}
              value={settings.voteNotificationMode ?? ""}
              onChange={(e) =>
                updateSetting(
                  "voteNotificationMode",
                  e.target.value || undefined,
                )
              }
              className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">{t("settings.notify.useDefault")}</option>
              <option value="instant">
                {tForm("settings.notify.instant")}
              </option>
              <option value="digest_15min">
                {t("settings.notify.digest15min")}
              </option>
              <option value="digest_hourly">
                {t("settings.notify.digestHourly")}
              </option>
              <option value="digest_daily">
                {t("settings.notify.digestDaily")}
              </option>
              <option value="off">{tForm("settings.notify.off")}</option>
            </select>
            <p className="text-xs text-muted-foreground mt-1">
              {tForm("settings.notify.overrideHint")}
            </p>
          </div>
        </div>
      </div>

      {/* Date d'expérience - Date de fin */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground">
          {t("settings.expiry.heading")}
        </h3>
        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <Calendar className="w-5 h-5 text-muted-foreground" />
            <div className="flex-1">
              <label
                htmlFor="expiresAt"
                className="block text-sm font-medium text-muted-foreground mb-1"
              >
                {t("settings.expiry.endDateLabel")}
              </label>
              <input
                type="datetime-local"
                id="expiresAt"
                value={
                  settings.expiresAt
                    ? new Date(settings.expiresAt!).toISOString().slice(0, 16)
                    : ""
                }
                onChange={(e) =>
                  updateSetting(
                    "expiresAt",
                    e.target.value
                      ? new Date(e.target.value).toISOString()
                      : undefined,
                  )
                }
                className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                min={new Date().toISOString().slice(0, 16)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {tForm("settings.expiry.hint")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAdvancedSettings = () => (
    <div className="space-y-6">
      {pollType !== "availability" && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground">
            {t("settings.voteMode.heading")}
          </h3>

          <div className="space-y-3">
            <Feature name="enableMaybeVotes">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <List className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium text-foreground">
                      {t("settings.voteMode.maybeTitle")}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t("settings.voteMode.maybeDescription")}
                    </p>
                  </div>
                </div>
                <label
                  htmlFor="allowMaybeVotes"
                  className="relative inline-flex items-center cursor-pointer"
                >
                  <input
                    type="checkbox"
                    id="allowMaybeVotes"
                    className="sr-only peer"
                    checked={settings.allowMaybeVotes ?? true}
                    onChange={(e) =>
                      updateSetting(
                        "allowMaybeVotes",
                        e.target.checked as never,
                      )
                    }
                  />
                  <div className="w-11 h-6 bg-input peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-ring/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-background after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-background after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
            </Feature>
          </div>
        </div>
      )}

      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground">
          {t("settings.access.heading")}
        </h3>

        {pollType !== "availability" && (
          <div className="space-y-3">
            {/* oneResponsePerPerson retiré (2026-07-23) : redondant avec
                l'unicité par fingerprint (upsert poll_id+stable_fingerprint). */}
            <Feature name="enableEditAfterSubmit">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Eye className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium text-foreground">
                      {t("settings.access.editAfterSubmitTitle")}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t("settings.access.editAfterSubmitDescription")}
                    </p>
                  </div>
                </div>
                <label
                  htmlFor="allowEditAfterSubmit"
                  className="relative inline-flex items-center cursor-pointer"
                >
                  <input
                    type="checkbox"
                    id="allowEditAfterSubmit"
                    className="sr-only peer"
                    checked={settings.allowEditAfterSubmit ?? false}
                    onChange={(e) =>
                      updateSetting("allowEditAfterSubmit", e.target.checked)
                    }
                  />
                  <div className="w-11 h-6 bg-input peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-ring/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-background after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-background after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
            </Feature>
          </div>
        )}
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          {/* Limite de réponses — désormais offerte aussi aux sondages de
              dates (2026-08-16). Elle y était invisible alors que les deux RPC
              l'appliquent depuis longtemps, et que la page de vote affiche
              déjà un écran « Sondage complet » basé dessus. À la place, un
              défaut caché `maxParticipants: 10` plafonnait tout sondage de
              dates par un contrôle client contournable — sans que personne
              l'ait choisi, et à rebours de la doctrine « illimité pour tous
              les plans ». */}
          {(pollType === "form" || pollType === "date") && (
            <>
              <div>
                <label
                  htmlFor="maxResponses"
                  className="block text-sm font-medium text-muted-foreground mb-1"
                >
                  {t("settings.maxResponses.label")}
                </label>
                <input
                  type="number"
                  id="maxResponses"
                  placeholder={t("settings.maxResponses.placeholder")}
                  min="1"
                  value={settings.maxResponses || ""}
                  onChange={(e) =>
                    updateSetting(
                      "maxResponses",
                      e.target.value ? parseInt(e.target.value) : undefined,
                    )
                  }
                  className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {t("settings.maxResponses.hint")}
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const renderEmailSettings = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground">
          {t("settings.email.confirmationHeading")}
        </h3>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Mail className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="font-medium text-foreground">
                  {t("settings.email.copyTitle")}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t("settings.email.copyDescription")}
                </p>
              </div>
            </div>
            <label
              htmlFor="sendEmailCopy"
              className="relative inline-flex items-center cursor-pointer"
            >
              <input
                type="checkbox"
                id="sendEmailCopy"
                className="sr-only peer"
                checked={settings.sendEmailCopy ?? false}
                onChange={(e) =>
                  updateSetting("sendEmailCopy", e.target.checked)
                }
              />
              <div className="w-11 h-6 bg-input peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-ring/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-background after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-background after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>

          {settings.sendEmailCopy && (
            <div className="ml-8">
              <label
                htmlFor="resultsCopyEmail"
                className="block text-sm font-medium text-muted-foreground mb-1"
              >
                {t("settings.email.addressLabel")}
              </label>
              <input
                type="email"
                id="resultsCopyEmail"
                placeholder={t("settings.email.addressPlaceholder")}
                value={settings.resultsCopyEmail || ""}
                onChange={(e) =>
                  updateSetting("resultsCopyEmail", e.target.value)
                }
                className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                required={settings.sendEmailCopy}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t("settings.email.addressRequiredHint")}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderVisibilitySettings = () => {
    // En mode "lien/QR" il n'y a pas d'emails connus : restreindre les
    // résultats par email n'a aucun sens, on force et on désactive l'option.
    const linkMode = audienceMode === "link";

    const currentVisibility = linkMode
      ? "public"
      : resultsVisibility || settings.resultsVisibility || "public";

    const handleChange = (vis: "restricted" | "public") => {
      if (linkMode) return;
      if (onResultsVisibilityChange) {
        onResultsVisibilityChange(vis);
      } else {
        updateSetting("resultsVisibility", vis);
      }
    };

    // « Qui peut voter » — réglage distinct de la visibilité des résultats.
    // Jusqu'au 2026-08-16 un seul contrôle portait le libellé « seuls les
    // participants invités peuvent voter » tout en écrivant resultsVisibility,
    // qui ne gouverne que la lecture des résultats : le texte ne décrivait pas
    // ce que le réglage faisait, et rien n'était appliqué côté serveur.
    const voteAccess = linkMode
      ? "public"
      : (settings.voteAccess as string) || "public";

    return (
      <div className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground">
            {t("settings.access.heading")}
          </h3>

          <div className="space-y-3">
            {[
              {
                value: "public",
                label: t("settings.access.publicLabel"),
                description: t("settings.access.publicDescription"),
              },
              {
                value: "invite",
                label: t("settings.access.inviteLabel"),
                description: t("settings.access.inviteDescription"),
              },
            ].map((option) => {
              const disabled = linkMode && option.value === "invite";
              return (
                <label
                  key={option.value}
                  htmlFor={`access-${option.value}`}
                  className={`flex items-center p-3 rounded-lg border border-border/50 transition-all ${
                    disabled
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer hover:bg-accent hover:border-border"
                  }`}
                >
                  <input
                    type="radio"
                    id={`access-${option.value}`}
                    name="voteAccess"
                    value={option.value}
                    checked={voteAccess === option.value}
                    disabled={disabled}
                    onChange={(e) =>
                      updateSetting("voteAccess", e.target.value)
                    }
                    className="h-5 w-5 text-primary focus:ring-ring border-input bg-input"
                  />
                  <div className="ml-4">
                    <div className="font-medium text-foreground">
                      {option.label}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {option.description}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>

          {voteAccess === "invite" && (
            <p className="text-xs text-warning">
              {t("settings.access.forwardWarning")}
            </p>
          )}
        </div>

        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground">
            {t("settings.visibility.heading")}
          </h3>

          {linkMode && (
            <p className="text-xs text-muted-foreground">
              {t("settings.visibility.linkModeNote")}
            </p>
          )}

          <div className="space-y-3">
            {[
              {
                value: "public",
                label: t("settings.visibility.publicLabel"),
                description: tForm("settings.visibility.publicDescription"),
              },
              {
                value: "restricted",
                label: t("settings.visibility.creatorOnlyLabel"),
                description: tForm(
                  "settings.visibility.creatorOnlyDescription",
                ),
              },
            ].map((option) => {
              const disabled = linkMode && option.value === "restricted";
              return (
                <label
                  key={option.value}
                  htmlFor={`vis-${option.value}`}
                  className={`flex items-center p-3 rounded-lg border border-border/50 transition-all ${
                    disabled
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer hover:bg-accent hover:border-border"
                  }`}
                >
                  <input
                    type="radio"
                    id={`vis-${option.value}`}
                    name="resultsVisibility"
                    value={option.value}
                    checked={currentVisibility === option.value}
                    disabled={disabled}
                    onChange={(e) =>
                      handleChange(e.target.value as "restricted" | "public")
                    }
                    className="h-5 w-5 text-primary focus:ring-ring border-input bg-input"
                  />
                  <div className="ml-4">
                    <div className="font-medium text-foreground">
                      {option.label}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {option.description}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Validation helper pour les emails des participants
  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const participantEmailsInfo = useMemo(() => {
    const raw = settings.inviteEmails || "";
    if (!raw.trim()) return { valid: [], invalid: [], total: 0 };
    const emails = raw
      .split(/[,;\n]+/)
      .map((e) => e.trim())
      .filter(Boolean);
    const valid = emails.filter((e) => EMAIL_REGEX.test(e));
    const invalid = emails.filter((e) => !EMAIL_REGEX.test(e));
    return { valid, invalid, total: emails.length };
  }, [settings.inviteEmails]);

  const setAudienceMode = (mode: AudienceMode) => {
    const next: Record<string, any> = {
      ...settings,
      audienceMode: mode,
    };
    if (mode !== "group") {
      next.audienceGroupId = undefined;
      next.audienceGroupName = undefined;
    }
    if (mode === "link") {
      // Pas d'emails attachés, on nettoie pour éviter un envoi fantôme
      next.inviteEmails = "";
      // Mode lien => résultats publics (cf. visibilité ci-dessous)
      next.resultsVisibility = "public";
    }
    onSettingsChange(next);
    if (mode === "link" && onResultsVisibilityChange) {
      onResultsVisibilityChange("public");
    }
  };

  const audienceOptions: {
    value: AudienceMode;
    label: string;
    description: string;
    icon: React.ReactNode;
  }[] = [
    {
      value: "group",
      label: t("settings.audience.groupLabel"),
      description: t("settings.audience.groupDescription"),
      icon: <Users className="w-5 h-5 text-purple-400" />, // color-exempt
    },
    {
      value: "emails",
      label: t("settings.audience.emailsLabel"),
      description: t("settings.audience.emailsDescription"),
      icon: <Mail className="w-5 h-5 text-blue-400" />, // color-exempt
    },
    {
      value: "link",
      label: t("settings.audience.linkLabel"),
      description: t("settings.audience.linkDescription"),
      icon: <Link2 className="w-5 h-5 text-green-400" />, // color-exempt
    },
  ];

  const renderAudienceSettings = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground">
          {t("settings.audience.heading")}
        </h3>
        <p className="text-sm text-muted-foreground">
          {t("settings.audience.subtitle")}
        </p>

        <div
          className="space-y-3"
          role="radiogroup"
          aria-label={t("settings.audience.heading")}
        >
          {audienceOptions.map((opt) => (
            <label
              key={opt.value}
              htmlFor={`audience-${opt.value}`}
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                audienceMode === opt.value
                  ? "border-primary bg-primary/10"
                  : "border-border/50 hover:bg-accent hover:border-border"
              }`}
              data-testid={`audience-option-${opt.value}`}
            >
              <input
                type="radio"
                id={`audience-${opt.value}`}
                name="audienceMode"
                value={opt.value}
                checked={audienceMode === opt.value}
                onChange={() => setAudienceMode(opt.value)}
                className="mt-1 h-5 w-5 text-primary focus:ring-ring border-input bg-input"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  {opt.icon}
                  <div className="font-medium text-foreground">{opt.label}</div>
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  {opt.description}
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {audienceMode === "group" && (
        <div className="space-y-3 pt-2 border-t border-border/50">
          <GroupSelector
            currentEmails={
              settings.inviteEmails
                ? settings.inviteEmails
                    .split(/[,;\n]+/)
                    .map((e: string) => e.trim())
                    .filter(Boolean)
                : []
            }
            selectedGroupId={settings.audienceGroupId}
            onSelectGroup={(emails, group) =>
              onSettingsChange({
                ...settings,
                audienceMode: "group",
                inviteEmails: emails.join(", "),
                audienceGroupId: group?.id,
                audienceGroupName: group?.name,
              })
            }
            currentPollData={currentPollData}
          />
        </div>
      )}

      {audienceMode === "emails" && (
        <div className="space-y-3 pt-2 border-t border-border/50">
          <label
            htmlFor="participantEmails"
            className="block text-sm font-medium text-muted-foreground"
          >
            {t("settings.audience.emailsToInviteLabel")}
          </label>
          <textarea
            id="participantEmails"
            placeholder={t("settings.audience.emailsToInvitePlaceholder")}
            className={`w-full px-3 py-2 border bg-background text-foreground rounded-md focus:outline-none focus:ring-2 ${
              participantEmailsInfo.invalid.length > 0
                ? "border-destructive/50 focus:ring-destructive"
                : participantEmailsInfo.valid.length > 0
                  ? "border-success/30 focus:ring-success"
                  : "border-input focus:ring-ring"
            }`}
            rows={3}
            onChange={(e) => updateSetting("inviteEmails", e.target.value)}
            value={settings.inviteEmails || ""}
          />

          <div className="flex items-center gap-3 text-xs">
            <span className="text-muted-foreground">
              {t("settings.audience.emailsSeparatorHint")}
            </span>
          </div>

          {participantEmailsInfo.total > 0 && (
            <div className="flex items-center gap-3 text-xs">
              {participantEmailsInfo.valid.length > 0 && (
                <span className="text-success flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  {t("settings.audience.validEmailsCount", {
                    count: participantEmailsInfo.valid.length,
                  })}
                </span>
              )}
              {participantEmailsInfo.invalid.length > 0 && (
                <span className="text-destructive">
                  ⚠{" "}
                  {t("settings.audience.invalidEmailsCount", {
                    count: participantEmailsInfo.invalid.length,
                    list: participantEmailsInfo.invalid.join(", "),
                  })}
                </span>
              )}
              <span className="text-muted-foreground">
                {t("settings.audience.invitationsOnFinalize")}
              </span>
            </div>
          )}
        </div>
      )}

      {audienceMode === "link" && (
        <div className="pt-2 border-t border-border/50">
          <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 border border-border/50">
            <Link2 className="w-5 h-5 text-muted-foreground mt-0.5" />
            <p className="text-sm text-muted-foreground">
              {tForm("settings.audience.linkModeInfo")}
            </p>
          </div>
        </div>
      )}
    </div>
  );

  const renderThemeSettings = () => (
    <ThemeSelector
      selectedThemeId={themeId || ""}
      onThemeChange={onThemeChange || (() => {})}
    />
  );

  const renderDisplaySettings = () => (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-muted-foreground mb-3">
        {t("settings.display.heading")}
      </label>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Option 1 : Classique */}
        <Button
          type="button"
          size="default"
          variant="outline"
          onClick={() =>
            onDisplayModeChange && onDisplayModeChange("all-at-once")
          }
          className={`p-4 h-auto rounded-lg border-2 text-left transition-all flex flex-col items-start ${
            displayMode === "all-at-once"
              ? "border-primary bg-primary/10"
              : "border-border hover:border-input bg-transparent text-muted-foreground"
          }`}
          data-testid="pollsettingsform-button"
        >
          <div className="flex items-center gap-2 mb-2 w-full">
            <List className="w-5 h-5 text-primary" />
            <span className="font-medium text-foreground">
              {t("settings.display.classicTitle")}
            </span>
            {displayMode === "all-at-once" && (
              <Check className="w-4 h-4 text-primary ml-auto" />
            )}
          </div>
          <p className="text-xs text-muted-foreground mb-2">
            {t("settings.display.classicSubtitle")}
          </p>
          <div className="text-xs text-muted-foreground space-y-1">
            <p>✓ {t("settings.display.classicPro1")}</p>
            <p>✓ {t("settings.display.classicPro2")}</p>
            <p>⚠ {t("settings.display.classicCon")}</p>
          </div>
        </Button>

        {/* Option 2 : Multi-step */}
        <Button
          type="button"
          size="default"
          variant="outline"
          onClick={() =>
            onDisplayModeChange && onDisplayModeChange("multi-step")
          }
          className={`p-4 h-auto rounded-lg border-2 text-left transition-all flex flex-col items-start ${
            displayMode === "multi-step"
              ? "border-primary bg-primary/10"
              : "border-border hover:border-input bg-transparent text-muted-foreground"
          }`}
          data-testid="pollsettingsform-button"
        >
          <div className="flex items-center gap-2 mb-2 w-full">
            <ArrowRight className="w-5 h-5 text-primary" />
            <span className="font-medium text-foreground">
              {t("settings.display.oneByOneTitle")}
            </span>
            <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded">
              {t("settings.display.typeformBadge")}
            </span>
            {displayMode === "multi-step" && (
              <Check className="w-4 h-4 text-primary ml-auto" />
            )}
          </div>
          <p className="text-xs text-muted-foreground mb-2">
            {t("settings.display.oneByOneSubtitle")}
          </p>
          <div className="text-xs text-muted-foreground space-y-1">
            <p>✓ {t("settings.display.oneByOnePro1")}</p>
            <p>✓ {t("settings.display.oneByOnePro2")}</p>
            <p>✓ {t("settings.display.oneByOnePro3")}</p>
          </div>
        </Button>
      </div>
    </div>
  );

  return (
    <div className="bg-card rounded-lg border border-border">
      <div className="border-b border-border bg-muted rounded-t-lg">
        <div
          className="flex space-x-2 px-4 overflow-x-auto"
          role="tablist"
          aria-label={t("settings.tablistAriaLabel")}
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`tab-${tab.id}`}
              aria-selected={activeTab === tab.id}
              aria-controls={`panel-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 px-4 border-b-2 font-medium text-sm flex items-center space-x-2 whitespace-nowrap transition-all duration-200 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card ${
                activeTab === tab.id
                  ? "border-primary text-primary bg-primary/10"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:border-border hover:bg-accent"
              }`}
              data-testid="pollsettingsform-button"
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div
        className="p-6"
        role="tabpanel"
        id={`panel-${activeTab}`}
        aria-labelledby={`tab-${activeTab}`}
      >
        {activeTab === "basic" && renderBasicSettings()}
        {activeTab === "display" && renderDisplaySettings()}
        {activeTab === "theme" && renderThemeSettings()}
        {activeTab === "advanced" && renderAdvancedSettings()}
        {/* Email settings moved to basic tab */}
        {activeTab === "visibility" && (
          <div className="space-y-8">
            {!hideAudience && renderAudienceSettings()}
            <div className={hideAudience ? "" : "border-t border-border pt-8"}>
              {renderVisibilitySettings()}
            </div>
          </div>
        )}
      </div>

      {/* Modal de restauration des sondages en attente */}
      {hasPendingPoll && pendingPoll && (
        <PendingPollRestoreModal
          isOpen={hasPendingPoll}
          pendingPoll={pendingPoll}
          onRestore={restorePoll}
          onDismiss={dismissPoll}
          isRestoring={isRestoring}
        />
      )}
    </div>
  );
}
